import { useState } from "react";
import { Dropdown, Modal, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { withTranslation, useTranslation } from "react-i18next";
import { AiOutlineCalculator } from "react-icons/ai";
import { BiInfoCircle, BiPencil, BiPlus, BiTrash } from "react-icons/bi";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import { EquationBuilderFor } from "../../../../core/constants/common";
import EmissionFactorService from "../../../../core/service/EmissionFactorService";
import RiskConfigService from "../../../../core/service/RiskConfigService";
import StandardService from "../../../../core/service/StandardService";
import DataSourceLinker from "../../Datasource/DataSourceLinker";
import EmissionFactorLinker from "../../EmissionFactor/EmissionFactorLinker";
import LinkDataSource from "../../RiskSettings/LinkDataSource/LinkDataSource";
import LinkEmissionFactor from "../../RiskSettings/LinkEmissionFactor/LinkEmissionFactor";
import EquationBuilder from "../EquationBuilder/EquationBuilder";
import "./TopicsMetricsDpsTable.scss";

const TopicsMetricsDpsTable = ({
  t,
  data = { topics: [] },
  isLoading = false,
  onEditTopic,
  onAddTopic,
  onDeleteTopic,
  onEditMetric,
  onAddMetric,
  onDeleteMetric,
  onEditDataPoint,
  onAddDataPoint,
  onDeleteDataPoint,
  stdId,
  currentcategory,
  onEquationUpdate,
  onEmissionLinkUpdate,
  onDataSourceLinkUpdate,
  showTopicEditIcon = true,
  colHideShow = {
    ecdCode: true,
    type: true,
    unit: true,
    possibleUoms: true
  },
  isFinalizeInProgress = false
}) => {
  const { i18n } = useTranslation();
  const [equationDropdownMetricId, setEquationDropdownMetricId] = useState(null);
  const [showDataSource, setShowDataSource] = useState(true);
  const [showDS, setShowDS] = useState(false);
  const [showEF, setShowEF] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [selectedDP, setSelectedDP] = useState("");
  const [selectedEFId, setSelectedEFId] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unlinkConfirmType, setUnlinkConfirmType] = useState("");
  const [selectedID, setSelectedID] = useState("");
  const [unlinkIsDataPoint, setUnlinkIsDataPoint] = useState(false);
  const [showDeleteTopicConfirm, setShowDeleteTopicConfirm] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);
  const [showDeleteMetricConfirm, setShowDeleteMetricConfirm] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState(null);
  const [showDeleteDataPointConfirm, setShowDeleteDataPointConfirm] = useState(false);
  const [dataPointToDelete, setDataPointToDelete] = useState(null);
  const toSentenceCase = (str) => {
    if (!str || typeof str !== "string") return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formatRelevantUoms = (uoms) => {
    if (!uoms) return "";
    if (Array.isArray(uoms)) {
      return uoms.join(", ");
    }
    if (typeof uoms === "string") {
      return uoms;
    }
    return String(uoms);
  };

  // Helper function to get localized name directly from localizedContents
  const getLocalizedName = (item) => {
    if (!item) return "";
    if (item.localizedContents && item.localizedContents.length > 0) {
      const translated = item.localizedContents.find(
        content => content.languageCode === i18n.language && content.fieldName === 'name'
      );
      if (translated && translated.translatedValue) {
        return translated.translatedValue;
      }
    }
    return item.name || "";
  };

  if (isLoading) {
    return (
      <div className="topics-metrics-dps-table-loading">
        <div className="text-center p-4">{t("Loading.label")}</div>
      </div>
    );
  }

  if (!data || !data.topics || data.topics.length === 0) {
    return (
      <div className="topics-metrics-dps-table-empty">
        <div className="text-center p-4">{t("Nodatafound.label")}</div>
      </div>
    );
  }

  const renderDataPointRow = (topic, metric, dataPoint, rowIndex) => {
    return (
      <tr key={`dp-${dataPoint.id}-${rowIndex}`} className="row-datapoint">
        <td className="cell-active">{dataPoint.code || ""}</td>
        <td className="cell-active data-point-cell">
          <div className="cell-content-wrapper">
            {getLocalizedName(dataPoint)}
            <div className="d-flex align-items-center gap-1 ms-2">
              {onEditDataPoint && dataPoint.id && (
                <BiPencil
                  className={`edit-icon ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    onEditDataPoint(dataPoint.id, metric.id, dataPoint);
                  }}
                  title={t("EditDataPoint.label")}
                />
              )}
              {onDeleteDataPoint && dataPoint.id && (
                <BiTrash
                  className={`edit-icon text-danger ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    handleDeleteDataPoint(dataPoint);
                  }}
                  title={t("DeleteDataPoint.label")}
                />
              )}
            </div>
          </div>
        </td>
        <td className="cell-active">{dataPoint.altText ?? ''}</td>
        <td className="cell-active">{dataPoint.helpText ?? ''}</td>
        <td className="cell-active">{dataPoint.unitOfMeasure || ""}</td>
        <td className="cell-active">{formatRelevantUoms(dataPoint.relevantUoms)}</td>
        <td className="cell-active">{toSentenceCase(dataPoint.frequency || "")}</td>
        <td className="cell-active">
          {dataPoint.groups
            ? Array.isArray(dataPoint.groups)
              ? dataPoint.groups.join(", ")
              : String(dataPoint.groups)
            : ""}
        </td>
        {/* <td className="cell-active">{""}</td> */}
        <td className="cell-active">
          <span className={`badge ${dataPoint.emission ? 'bg-badge-Active' : 'bg-badge-Inactive'}`}>
            {dataPoint.emission ? "Yes" : "No"}
          </span>
        </td>
        <td className="cell-active">
          <ul className="d-flex align-items-center mb-0 p-0" style={{ listStyle: 'none' }}>
            <DataSourceLinker
              item={{
                ...dataPoint,
                riskDataSource: dataPoint.riskDataSource || dataPoint.datasourceLink
              }}
              openDataSourceLink={(itemId, dataSourceId) => {
                if (isFinalizeInProgress) return;
                handleDataSourceLink(null, itemId, dataSourceId || "", true);
              }}
              unlinkDataSource={(item) => {
                if (isFinalizeInProgress) return;
                handleDataSourceUnlink(item, true);
              }}
              disabled={isFinalizeInProgress}
            />
          </ul>
        </td>
        <td className="cell-active">
          <ul className="d-flex align-items-center mb-0 p-0" style={{ listStyle: 'none' }}>
            <EmissionFactorLinker
              item={dataPoint}
              fromDataPointPage={true}
              onSaveSuccess={() => {
                if (onEmissionLinkUpdate) {
                  onEmissionLinkUpdate();
                }
              }}
              openEmissionFactorLink={(itemId, emissionFactorId) => {
                if (isFinalizeInProgress) return;
                handleEmissionFactorLink(itemId, emissionFactorId || "", true);
              }}
              unlinkEmissionFactor={(dpId) => {
                if (isFinalizeInProgress) return;
                handleEmissionFactorUnlink(dpId, true);
              }}
              disabled={isFinalizeInProgress}
            />
          </ul>
        </td>
      </tr>
    );
  };

  const handleEquationUpdate = (eq) => {
    // EquationBuilder saves the equation automatically via API
    // We just need to refresh the table data
    if (onEquationUpdate) {
      onEquationUpdate();
    }
    setEquationDropdownMetricId(null);
  };

  const handleDataSourceLink = (e, itemId, dataSourceId = "", isDataPoint = false) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedMetric(itemId);
    setSelectedDataSource(dataSourceId || "");
    setSelectedDP(isDataPoint ? itemId : "");
    setShowDataSource(false);
    setShowDS(true);
    setShowEF(false);
  };

  const handleDataSourceUnlink = (item, isDataPoint = false) => {
    setUnlinkIsDataPoint(isDataPoint);
    showConfirm(item.id, 'ds');
  };

  const handleEmissionFactorLink = (itemId, emissionFactorId = "", isDataPoint = false) => {
    if (isDataPoint) {
      setSelectedDP(itemId);
      setSelectedEFId(emissionFactorId);
    } else {
      setSelectedMetric(itemId);
      setSelectedEFId(emissionFactorId);
    }
    setShowDataSource(false);
    setShowDS(false);
    setShowEF(true);
  };

  const handleEmissionFactorUnlink = (itemId, isDataPoint = false) => {
    setUnlinkIsDataPoint(isDataPoint);
    showConfirm(itemId, 'em');
  };

  const showConfirm = (id, confirmType) => {
    setSelectedID(id);
    setShowConfirmation(true);
    setUnlinkConfirmType(confirmType);
  };

  const confirmDelete = (val) => {
    setShowConfirmation(false);
    if (val) {
      if (unlinkConfirmType === 'em') {
        handleEmissionFactorUnlinkAction(selectedID, unlinkIsDataPoint);
      } else if (unlinkConfirmType === 'ds') {
        handleDataSourceUnlinkAction(selectedID, unlinkIsDataPoint);
      }
    }
    setSelectedID("");
    setUnlinkConfirmType("");
    setUnlinkIsDataPoint(false);
  };

  const handleDeleteTopic = (topic) => {
    setTopicToDelete(topic);
    setShowDeleteTopicConfirm(true);
  };

  const handleDeleteTopicConfirm = (confirmed) => {
    setShowDeleteTopicConfirm(false);
    if (confirmed && topicToDelete && stdId) {
      StandardService.deleteTopic(stdId, topicToDelete.id, false)
        .then(res => {
          // Check response body status first (backend returns {status: "Failed", message: "..."})
          if (res.data?.status === 'Failed') {
            const errorMessage = res.data?.message || t("DeleteTopicError.label") || "Error deleting topic";
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else if (res.status === 200 || res.status === 204) {
            toast.success(t("TopicDeletedSuccessfully.label") || "Topic deleted successfully", {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onDeleteTopic) {
              onDeleteTopic();
            }
          } else {
            // Fallback for other non-2xx statuses without specific 'Failed' status in body
            const errorMessage = res.data?.message || res.data?.detail || t("DeleteTopicError.label") || "Error deleting topic";
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        })
        .catch(err => {
          console.error('Delete topic error:', err);
          const errorMessage = err.response?.data?.message ||
            err.response?.data?.detail ||
            (err.response?.data?.status === 'Failed' ? err.response?.data?.message : null) ||
            t("DeleteTopicError.label") ||
            "Error deleting topic. Please check network or server logs.";
          toast.error(errorMessage, {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    }
    setTopicToDelete(null);
  };

  const handleDeleteMetric = (metric) => {
    setMetricToDelete(metric);
    setShowDeleteMetricConfirm(true);
  };

  const handleDeleteMetricConfirm = (confirmed) => {
    setShowDeleteMetricConfirm(false);
    if (confirmed && metricToDelete) {
      StandardService.deleteMetric(metricToDelete.id, false)
        .then(res => {
          // Check response body status first (backend returns {status: "Failed", message: "..."})
          if (res.data?.status === 'Failed') {
            const errorMessage = res.data?.message || t("DeleteMetricError.label") || "Error deleting metric";
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else if (res.status === 200 || res.status === 204) {
            toast.success(t("MetricDeletedSuccessfully.label") || "Metric deleted successfully", {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onDeleteMetric) {
              onDeleteMetric();
            }
          } else {
            // Fallback for other non-2xx statuses without specific 'Failed' status in body
            const errorMessage = res.data?.message || res.data?.detail || t("DeleteMetricError.label") || "Error deleting metric";
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        })
        .catch(err => {
          console.error('Delete metric error:', err);
          const errorMessage = err.response?.data?.message ||
            err.response?.data?.detail ||
            (err.response?.data?.status === 'Failed' ? err.response?.data?.message : null) ||
            t("DeleteMetricError.label") ||
            "Error deleting metric. Please check network or server logs.";
          toast.error(errorMessage, {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    }
    setMetricToDelete(null);
  };

  const handleDeleteDataPoint = (dataPoint) => {
    setDataPointToDelete(dataPoint);
    setShowDeleteDataPointConfirm(true);
  };

  const handleDeleteDataPointConfirm = (confirmed) => {
    setShowDeleteDataPointConfirm(false);
    if (confirmed && dataPointToDelete) {
      StandardService.deleteDataPoint(dataPointToDelete.id, false)
        .then(res => {
          // Check response body status first (backend returns {status: "Failed", message: "..."})
          if (res.data?.status === 'Failed') {
            const errorMessage = res.data?.message || t("DeleteDataPointError.label") || "Error deleting data point";
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else if (res.status === 200 || res.status === 204) {
            toast.success(t("DataPointDeletedSuccessfully.label") || "Data point deleted successfully", {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onDeleteDataPoint) {
              onDeleteDataPoint();
            }
          } else {
            // Fallback for other non-2xx statuses without specific 'Failed' status in body
            const errorMessage = res.data?.message || res.data?.detail || t("DeleteDataPointError.label") || "Error deleting data point";
            toast.error(errorMessage, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        })
        .catch(err => {
          console.error('Delete data point error:', err);
          const errorMessage = err.response?.data?.message ||
            err.response?.data?.detail ||
            (err.response?.data?.status === 'Failed' ? err.response?.data?.message : null) ||
            t("DeleteDataPointError.label") ||
            "Error deleting data point. Please check network or server logs.";
          toast.error(errorMessage, {
            position: toast.POSITION.TOP_RIGHT,
          });
        });
    }
    setDataPointToDelete(null);
  };

  const handleEmissionFactorUnlinkAction = (id, isDataPoint = false) => {
    if (isDataPoint) {
      EmissionFactorService.unlinkEmissionFactorDP(id)
        .then((res) => {
          if (res.status === 200) {
            toast.success("Success", {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onEmissionLinkUpdate) {
              onEmissionLinkUpdate();
            }
          }
        })
        .catch((err) => {
        });
    } else {
      EmissionFactorService.unlinkEmissionFactorMetric(id)
        .then((res) => {
          if (res.status === 200) {
            toast.success("Success", {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onEmissionLinkUpdate) {
              onEmissionLinkUpdate();
            }
          }
        })
        .catch((err) => {
        });
    }
  };

  const handleDataSourceUnlinkAction = (itemId, isDataPoint = false) => {
    const paramType = isDataPoint ? 'dataPoint' : 'metric';
    RiskConfigService.unlinkDatasource(stdId, itemId, paramType)
      .then((res) => {
        if (res.status === 200) {
          toast.success("Success", {
            position: toast.POSITION.TOP_RIGHT,
          });
          if (onDataSourceLinkUpdate) {
            onDataSourceLinkUpdate();
          }
        }
      })
      .catch((err) => {
      });
  };

  const handleDataSourceCallback = (val) => {
    setShowDataSource(true);
    setShowDS(false);
    setShowEF(false);
    // Only refresh table data if a link was actually saved (val === true)
    // If val === false, it means the user just closed/discarded without saving
    if (val && onDataSourceLinkUpdate) {
      onDataSourceLinkUpdate();
    }
    setUnlinkConfirmType('');
  };

  const handleEFCallback = (val) => {
    setShowDataSource(true);
    setShowDS(false);
    setShowEF(false);
    setSelectedMetric(null);
    setSelectedEFId("");
    // Only refresh table data if a link was actually saved (val === true)
    // If val === false, it means the user just closed/discarded without saving
    if (val && onEmissionLinkUpdate) {
      onEmissionLinkUpdate();
    }
    setUnlinkConfirmType('');
  };



  // Calculate total rows for a topic (metrics + data points)
  const calculateTopicRowCount = (topic) => {
    if (!topic.metrics || topic.metrics.length === 0) {
      return 1; // Just the topic row itself
    }

    let count = 0;
    topic.metrics.forEach((metric) => {
      // Use dataPointCount if available, otherwise check metricDataPoints array
      const dataPointCount = metric.dataPointCount || 0;
      if (dataPointCount > 0) {
        // Count metric row (1) + all data point rows (dataPointCount)
        count += 1 + dataPointCount;
      } else {
        // Only the metric row itself
        count += 1;
      }
    });
    console.log('Topic:', topic.name, '| Count:', count, '| Metrics:', topic.metrics.length, '| ChildrenCount:', topic.childrenCount);
    return count;
  };

  const renderTopicRows = (topic, topicIndex) => {
    // Calculate colSpan based on visible columns (19 total columns - 1 for Topic column - hidden columns)
    const hiddenColsCount =
      (!colHideShow.ecdCode ? 1 : 0) +
      (!colHideShow.type ? 1 : 0) +
      (!colHideShow.unit ? 1 : 0) +
      (!colHideShow.possibleUoms ? 1 : 0);
    const colSpan = 18 - hiddenColsCount;

    // Calculate rowSpan: 1 (topic row) + all metric/data point rows
    const metricRowsCount = topic.metrics && topic.metrics.length > 0
      ? calculateTopicRowCount(topic)
      : 0;
    const topicRowspan = 1 + metricRowsCount;

    // Always render topic row first
    const topicRow = (
      <tr key={`topic-${topic.id}-${topicIndex}`} className="row-topic">
        <td className="cell-active topic-cell" rowSpan={topicRowspan}>
          <div className="cell-content-wrapper">
            <span>{getLocalizedName(topic)}</span>
            <div className="d-flex align-items-center gap-1 ms-2">
              {onEditTopic && topic.id && showTopicEditIcon && (
                <BiPencil
                  className={`edit-icon ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    onEditTopic(topic.id, stdId, currentcategory);
                  }}
                  title={t("EditTopic.label")}
                />
              )}
              {onAddMetric && topic.id && showTopicEditIcon && (
                <BiPlus
                  className={`edit-icon ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    onAddMetric(topic.id, stdId);
                  }}
                  title={t("AddMetric.label")}
                />
              )}
              {onDeleteTopic && topic.id && showTopicEditIcon && (
                <BiTrash
                  className={`edit-icon text-danger ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    handleDeleteTopic(topic);
                  }}
                  title={t("DeleteTopic.label")}
                />
              )}
            </div>
          </div>
        </td>
        {/* <td colSpan={colSpan} className="cell-disabled">{""}</td> */}
      </tr>
    );

    // If no metrics, return only the topic row
    if (!topic.metrics || topic.metrics.length === 0) {
      return topicRow;
    }

    // Render all metrics below the topic row (without topic cell in metric rows)
    const metricRows = topic.metrics.flatMap((metric, metricIndex) => {
      const rows = renderMetricRow(topic, metric, `${topicIndex}-${metricIndex}`, showTopicEditIcon, false, 1);
      // Ensure we always return an array
      return Array.isArray(rows) ? rows : [rows];
    });

    // Return topic row first, then all metric rows
    return [topicRow, ...metricRows];
  };

  const renderMetricRow = (topic, metric, rowIndex, showTopicEditIcon = false, showTopicCell = false, topicRowspan = 1) => {
    const isQuantitativeNoDP = metric.category === 'QUANTITATIVE' && (!metric.metricDataPoints || metric.metricDataPoints.length === 0);
    const hasDataPoints = metric.metricDataPoints && metric.metricDataPoints.length > 0;

    // Calculate rowspan for metric name and metric-related columns when there are data points
    const metricRowspan = hasDataPoints ? 1 + metric.metricDataPoints.length : 1;

    // Always render the metric row first
    const metricRow = (
      <tr key={`metric-${metric.id}-${rowIndex}`} className="row-metric">

        <td className="cell-active metric-cell" rowSpan={metricRowspan}>
          <div className="cell-content-wrapper">
            <span>{getLocalizedName(metric)}</span>
            <div className="d-flex align-items-center gap-1 ms-2">
              {onEditMetric && metric.id && (
                <BiPencil
                  className={`edit-icon ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    onEditMetric(metric.id, topic.id, stdId);
                  }}
                  title={t("EditMetric.label")}
                />
              )}
              {onAddDataPoint && metric.id && (
                <BiPlus
                  className={`edit-icon ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    onAddDataPoint(metric.id);
                  }}
                  title={t("AddDataPoint.label")}
                />
              )}
              {onDeleteMetric && metric.id && (
                <BiTrash
                  className={`edit-icon text-danger ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isFinalizeInProgress) return;
                    handleDeleteMetric(metric);
                  }}
                  title={t("DeleteMetric.label")}
                />
              )}
            </div>
          </div>
        </td>
        <td className="cell-disabled equation-cell-td" rowSpan={metricRowspan}>
          {metric.category === 'QUANTITATIVE' && (
            <div className="AddEquation_box equation-dropdown-cell">

              <Dropdown
                show={equationDropdownMetricId === metric.id}
                onToggle={(nextShow) => {
                  if (isFinalizeInProgress) return;
                  setEquationDropdownMetricId(nextShow ? metric.id : null);
                }}
                autoClose={false}
              >
                <Dropdown.Toggle
                  variant="ShowEquation"
                  id={`equation-dropdown-${metric.id}`}
                  disabled={isFinalizeInProgress}
                  className={`equation-dropdown-toggle ${isFinalizeInProgress ? "disabled-icon" : ""}`}
                >
                  {metric.equation?.expression ? (
                    <span className="equation-expression-inner">
                      <AiOutlineCalculator className="equation-expression-icon flex-shrink-0" aria-hidden />
                      <span className="equation-expression-text">{metric.equation.expression}</span>
                    </span>
                  ) : (
                    <>
                      <BiPlus className="me-2" />
                      {t("Equation.label")}
                    </>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="p-0 equation-dropdown-menu"
                  popperConfig={{ strategy: "fixed" }}
                >
                  <EquationBuilder
                    stdId={stdId}
                    metricId={metric.id}
                    topicId={topic.id}
                    discardBtnClick={() => setEquationDropdownMetricId(null)}
                    equationObj={metric.equation}
                    updatedEquationObj={handleEquationUpdate}
                  />
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </td>
        {colHideShow.ecdCode && <td className="cell-active" >{metric.code || ""}</td>}
        <td className="cell-disabled"></td>
        {colHideShow.type && <td className="cell-active" rowSpan={metricRowspan}>{toSentenceCase(metric.category) || ""}</td>}

        <td className="cell-disabled"></td>
        <td className="cell-disabled"></td>
        {/* <td className="cell-disabled"></td> */}
        {colHideShow.unit && <td className="cell-active" >{metric.unitOfMeasure || ""}</td>}
        {colHideShow.possibleUoms && <td className="cell-active possible-uoms-cell" >{formatRelevantUoms(metric.relevantUoms)}</td>}
        <td className="cell-disabled"></td>
        <td className="cell-disabled"></td>

        <td className="cell-disabled">
          {metric.category === 'QUANTITATIVE' && (
            <span className={`badge ${metric.emission ? 'bg-badge-Active' : 'bg-badge-Inactive'}`}>
              {metric.emission ? "Yes" : "No"}
            </span>
          )}
        </td>
        <td className="cell-disabled" rowSpan={metricRowspan}>
          <span className={`badge ${metric.kpi ? 'bg-badge-Active' : 'bg-badge-Inactive'}`}>
            {metric.kpi ? "Yes" : "No"}
          </span>
        </td>
        <td className="cell-disabled">
          {isQuantitativeNoDP && (
            <ul className="d-flex align-items-center mb-0 p-0" style={{ listStyle: 'none' }}>
              <DataSourceLinker
                item={{
                  ...metric,
                  riskDataSource: metric.riskDataSource || metric.datasourceLink
                }}
                openDataSourceLink={(itemId, dataSourceId) => {
                  if (isFinalizeInProgress) return;
                  handleDataSourceLink(null, itemId, dataSourceId || "", false);
                }}
                unlinkDataSource={(item) => {
                  if (isFinalizeInProgress) return;
                  handleDataSourceUnlink(item, false);
                }}
                disabled={isFinalizeInProgress}
              />
            </ul>
          )}
        </td>
        <td className="cell-disabled">
          {/* {metric.category === 'QUANTITATIVE' && (
            <ul className="d-flex align-items-center mb-0 p-0" style={{ listStyle: 'none' }}>
              <EmissionFactorLinker
                item={metric}
                onSaveSuccess={() => {
                  if (onEmissionLinkUpdate) {
                    onEmissionLinkUpdate();
                  }
                }}
                openEmissionFactorLink={(itemId, emissionFactorId) => {
                  if (isFinalizeInProgress) return;
                  handleEmissionFactorLink(itemId, emissionFactorId, false);
                }}
                unlinkEmissionFactor={(itemId) => {
                  if (isFinalizeInProgress) return;
                  handleEmissionFactorUnlink(itemId, false);
                }}
                disabled={isFinalizeInProgress}
              />
            </ul>
          )} */}
        </td>
      </tr>
    );

    // If metric has datapoints, render them after the metric row
    if (hasDataPoints) {
      const dataPointRows = metric.metricDataPoints.map((dataPoint, dpIndex) =>
        renderDataPointRow(topic, metric, dataPoint, `${rowIndex}-${dpIndex}`)
      );
      return [metricRow, ...dataPointRows];
    }

    // If no datapoints, return just the metric row
    return metricRow;
  };

  return (
    <div className="table-holder table-responsive mt-0">
      <Table responsive="sm" bordered>
        <thead>
          <tr>
            <th>
              <div className="header-content-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="topic-tooltip">{t("Topic.tooltip.label")}</Tooltip>}
                >
                  <span className="d-flex align-items-center gap-1">{t("Topic.label")}
                    <BiInfoCircle size={16} aria-label={t("Topic.tooltip.label")} />
                  </span>
                </OverlayTrigger>
                {onAddTopic && (
                  <BiPlus
                    className={`add-icon ${isFinalizeInProgress ? 'disabled-icon' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isFinalizeInProgress) return;
                      onAddTopic(stdId, currentcategory);
                    }}
                    title={t("Add.label")}
                  />
                )}
              </div>
            </th>
            <th>
              <div className="header-content-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="metrics-tooltip">{t("Metrics.tooltip.label")}</Tooltip>}
                >
                  <span className="d-flex align-items-center gap-1">{t("Metrics.label")}
                    <BiInfoCircle size={16} aria-label={t("Metrics.tooltip.label")} />
                  </span>
                </OverlayTrigger>
              </div>
            </th>
            <th>
              <div className="header-content-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="equation-tooltip">{t("Equation.tooltip.label")}</Tooltip>}
                >
                  <span className="d-flex align-items-center gap-1">{t("Equation.label")}
                    <BiInfoCircle size={16} aria-label={t("Equation.tooltip.label")} />
                  </span>
                </OverlayTrigger>
              </div>
            </th>
            {colHideShow.ecdCode && <th>{t("Code.label")}</th>}

            <th>
              <div className="header-content-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="datapoints-tooltip">{t("DataPoints.tooltip.label")}</Tooltip>}
                >
                  <span className="d-flex align-items-center gap-1">{t("DataPoints.label")}
                    <BiInfoCircle size={16} aria-label={t("DataPoints.tooltip.label")} />
                  </span>
                </OverlayTrigger>
              </div>
            </th>
            {colHideShow.type && <th>{t("Type.label")}</th>}
            <th>{t("AltText.label")}</th>
            <th>{t("HelpText.label")}</th>
            {colHideShow.unit && <th>{t("Unit.label")}</th>}
            {colHideShow.possibleUoms && <th>{t("PossibleUOMs.label")}</th>}
            <th>{t("Frequency.label")}</th>
            <th>{t("DataOwnerGroup.label")}</th>
            <th>{t("Emission.label")}</th>
            <th>{t("KPI.label")}</th>
            <th>{t("LinkToDataSource.label")}</th>
            <th>{t("LinkToEMF.label")}</th>
          </tr>
        </thead>
        <tbody>
          {data.topics.flatMap((topic, topicIndex) => renderTopicRows(topic, topicIndex))}
        </tbody>
      </Table>

      {/* DataSource Linking Modal */}
      {showDS && selectedMetric && (
        <Modal
          show={showDS}
          onHide={() => {
            // Close modal without refreshing when user clicks X or clicks outside
            setShowDataSource(true);
            setShowDS(false);
            setShowEF(false);
          }}
          size="lg"
          aria-labelledby="datasource-link-modal-title"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="h5" id="datasource-link-modal-title">
              {t("LinkToDataSource.label")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="LinkDataSource_body-tableView">
            <LinkDataSource
              regulation={stdId}
              paramId={selectedMetric}
              dsId={selectedDataSource}
              handleDataSourceCallback={handleDataSourceCallback}
              configType={'KPI'}
              paramType={selectedDP ? 'dataPoint' : 'metric'}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Emission Factor Linking Modal */}
      {showEF && (selectedDP || selectedMetric) && (
        <Modal
          show={showEF}
          onHide={() => {
            // Close modal without refreshing when user clicks X or clicks outside
            setShowDataSource(true);
            setShowDS(false);
            setShowEF(false);
            setSelectedMetric(null);
            setSelectedEFId("");
          }}
          size="lg"
          aria-labelledby="emission-factor-link-modal-title"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title id="emission-factor-link-modal-title">
              {t("LinkEmissionFactor.label")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LinkEmissionFactor
              dataPointId={selectedDP || selectedMetric}
              handleEFCallback={handleEFCallback}
              linkType={selectedDP ? 'dp' : 'metric'}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Unlink Confirmation Modal */}
      {showConfirmation && (
        <ConfirmationModal
          modalShow={true}
          messageText={
            unlinkConfirmType === "em"
              ? t("UnlinkEmissionFactorConfirmation.label")
              : t("Unlinkdatasourceconfirmation.label")
          }
          callbackModal={confirmDelete}
          confirmBtn={t("Confirm.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}

      {/* Delete Topic Confirmation Modal */}
      {showDeleteTopicConfirm && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("DeleteTopicConfirmation.label") || `Are you sure you want to delete the topic "${getLocalizedName(topicToDelete)}"?`}
          title={t("DeleteTopic.label") || "Delete Topic"}
          confirmBtn={t("Delete.label") || "Delete"}
          CancelBtn={t("Cancel.label") || "Cancel"}
          callbackModal={handleDeleteTopicConfirm}
        />
      )}

      {/* Delete Metric Confirmation Modal */}
      {showDeleteMetricConfirm && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("ConfirmDeleteMetricMsg.label") || `Are you sure you want to delete the metric "${getLocalizedName(metricToDelete)}"?`}
          title={t("DeleteMetric.label") || "Delete Metric"}
          confirmBtn={t("Delete.label") || "Delete"}
          CancelBtn={t("Cancel.label") || "Cancel"}
          callbackModal={handleDeleteMetricConfirm}
        />
      )}

      {/* Delete Data Point Confirmation Modal */}
      {showDeleteDataPointConfirm && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("ConfirmDeleteDPMsg.label") || `Are you sure you want to delete the data point "${getLocalizedName(dataPointToDelete)}"?`}
          title={t("DeleteDataPoint.label") || "Delete Data Point"}
          confirmBtn={t("Delete.label") || "Delete"}
          CancelBtn={t("Cancel.label") || "Cancel"}
          callbackModal={handleDeleteDataPointConfirm}
        />
      )}
    </div>
  );
};

export default withTranslation()(TopicsMetricsDpsTable);
